import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import EventIcon from "@mui/icons-material/Event";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import PersonIcon from "@mui/icons-material/Person";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, Chip, CircularProgress, Divider, ListItem, ListItemAvatar, Paper, Typography } from "@mui/material";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { ChatMessage } from "../../types";
import { AgentAvatar } from "../AgentAvatar";
import { TimezoneUtils } from "../../utils/timezoneUtils";
import { DateUtils } from "../../utils/dateUtils";
import { EmailActionRenderer, CoachPlanActionRenderer, PriorityActionRenderer, TodoActionRenderer, CalendarActionRenderer, CalendarMoveActionRenderer, GenericActionRenderer } from "./actionRenderers";

interface MessageItemProps {
  message: ChatMessage;
  onExecuteAction?: (actionId: string) => void;
  onIgnoreAction?: (actionId: string) => void;
  executingActions?: Set<string>;
  agentNames?: { [agentId: string]: string };
  agentAvatars?: { [agentId: string]: string | undefined };
  userTimezone?: string;
}

const getMessageIcon = (type: string) => {
  switch (type) {
    case "user":
      return <PersonIcon />;
    case "agent":
      return <SmartToyIcon />;
    case "event":
      return <EventIcon />;
    case "action":
      return <PlayArrowIcon />;
    case "source_request":
      return <InfoIcon />;
    case "source_response":
      return <CheckCircleIcon />;
    case "action_proposal":
      return <ScheduleIcon />;
    case "action_execution":
      return <PlayArrowIcon />;
    case "system":
    default:
      return <InfoIcon />;
  }
};

const getMessageColor = (type: string) => {
  switch (type) {
    case "user":
      return "#1976d2";
    case "agent":
      return "#388e3c";
    case "event":
      return "#f57c00";
    case "action":
      return "#d32f2f";
    case "source_request":
      return "#2196f3";
    case "source_response":
      return "#4caf50";
    case "action_proposal":
      return "#ff9800";
    case "action_execution":
      return "#d32f2f";
    case "system":
    default:
      return "#757575";
  }
};

function toDate(ts: any): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "string" || typeof ts === "number") return new Date(ts);
  if (typeof ts === "object" && ts._seconds) return new Date(ts._seconds * 1000);
  return null;
}

// Basic markdown renderer (bold, italics, links, line breaks)
function renderMarkdown(text: string) {
  if (!text) return null;
  // Replace **bold**
  let html = text.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
  // Replace *italic*
  html = html.replace(/\*(.*?)\*/g, "<i>$1</i>");
  // Replace [text](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // Replace line breaks
  html = html.replace(/\n/g, "<br/>");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

// Event message component - centered line design
const EventMessage: React.FC<{ message: ChatMessage; userTimezone?: string }> = ({ message, userTimezone }) => {
  const [expanded, setExpanded] = useState(false);
  const date = toDate(message.timestamp);
  const metadata = message.metadata || {};

  return (
    <ListItem sx={{ px: 0, py: 1 }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Centered event line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "grey.100",
            border: "1px solid",
            borderColor: "grey.300",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "grey.200",
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <EventIcon sx={{ fontSize: 16, color: "grey.600" }} />
          <Typography variant="caption" color="text.secondary">
            {message.content}
          </Typography>
          {date && (
            <Typography variant="caption" color="text.secondary">
              {userTimezone
                ? TimezoneUtils.formatDateInTimezone(date, userTimezone, {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : date.toLocaleTimeString()}
            </Typography>
          )}
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              color: "grey.600",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </Box>

        {/* Expandable details */}
        {expanded && (
          <Paper sx={{ mt: 1, p: 2, width: "100%", maxWidth: 600 }}>
            <Typography variant="subtitle2" gutterBottom>
              Event Details
            </Typography>
            <Typography variant="body2" gutterBottom>
              {message.content}
            </Typography>

            {/* Event metadata */}
            {metadata.eventType && <Chip label={`Type: ${metadata.eventType}`} size="small" color="warning" variant="outlined" sx={{ mr: 1, mb: 1 }} />}

            {/* Event data */}
            {metadata.data && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="caption">View Event Data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ fontSize: "0.75rem", overflow: "auto" }}>{JSON.stringify(metadata.data, null, 2)}</pre>
                </AccordionDetails>
              </Accordion>
            )}
          </Paper>
        )}
      </Box>
    </ListItem>
  );
};

// Action message component - different designs for executed vs proposed
const ActionMessage: React.FC<{
  message: ChatMessage;
  onExecuteAction?: (actionId: string) => void;
  onIgnoreAction?: (actionId: string) => void;
  executingActions?: Set<string>;
}> = ({ message, onExecuteAction, onIgnoreAction, executingActions }) => {
  const [expanded, setExpanded] = useState(false);
  const [executionOverridden, setExecutionOverridden] = useState(false);
  const date = toDate(message.timestamp);
  const metadata = message.metadata || {};

  // Determine action status based on message type and metadata
  const hasResult = metadata.result !== undefined && metadata.result !== null;
  const isExecuted = message.type === "action_execution" || metadata.actionStatus === "executed" || metadata.status === "completed" || hasResult;
  const isProposed = !isExecuted && (message.type === "action_proposal" || metadata.actionStatus === "proposed" || metadata.actionStatus === "pending" || metadata.status === "proposed");
  const isExecuting = metadata.status === "executing";
  const isFailed = metadata.status === "failed";
  const isIgnored = metadata.actionStatus === "ignored";

  // Helper to open Gmail compose with prefilled fields
  const openGmailDraft = (to: string, subject: string, body: string) => {
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  // Collapsed view for ignored actions (similar to events)
  if (isIgnored) {
    return (
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            sx={{
              p: 1,
              maxWidth: 400,
              width: "100%",
              bgcolor: "grey.100",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CloseIcon sx={{ color: "grey.500", fontSize: 16 }} />
            <Typography variant="body2" sx={{ flex: 1, color: "text.secondary" }}>
              Action Ignored:{" "}
              {message.actionType
                ?.replace(/\./g, " - ")
                .replace(/([A-Z])/g, " $1")
                .trim() ||
                metadata.actionType
                  ?.replace(/\./g, " - ")
                  .replace(/([A-Z])/g, " $1")
                  .trim()}
            </Typography>
            {date && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {date.toLocaleTimeString()}
              </Typography>
            )}
          </Paper>
        </Box>
      </ListItem>
    );
  }

  return (
    <ListItem sx={{ px: 0, py: 1 }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Paper
          sx={{
            p: 2,
            maxWidth: 600,
            width: "100%",
            bgcolor: isExecuted ? "success.light" : isProposed ? "warning.light" : "grey.100",
            border: "1px solid",
            borderColor: isExecuted ? "success.main" : isProposed ? "warning.main" : "grey.300",
            borderRadius: 2,
            color: isExecuted ? "common.white" : undefined, // Make text white on green for executed
          }}
        >
          {/* Action header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {isExecuted ? <CheckCircleIcon sx={{ color: "common.white" }} /> : isProposed ? <ScheduleIcon sx={{ color: "warning.main" }} /> : <PlayArrowIcon sx={{ color: "grey.600" }} />}
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {isExecuted ? "Action Executed" : isProposed ? "Action Proposed" : "Action"}:{" "}
              {message.actionType
                ?.replace(/\./g, " - ")
                .replace(/([A-Z])/g, " $1")
                .trim() ||
                metadata.actionType
                  ?.replace(/\./g, " - ")
                  .replace(/([A-Z])/g, " $1")
                  .trim()}
            </Typography>
            {date && (
              <Typography variant="caption" sx={{ color: isExecuted ? "common.white" : "text.secondary" }}>
                {date.toLocaleTimeString()}
              </Typography>
            )}
          </Box>

          {/* Action content */}
          {(() => {
            // Use metadata.actionType and metadata as the data source
            const actionType = message.actionType || metadata.actionType;
            const actionData = message.data || metadata;

            if (actionType === "Email.Draft.Create" && actionData) {
              return (
                <EmailActionRenderer
                  actionData={actionData}
                  onExecuteAction={onExecuteAction}
                  actionId={metadata.actionId}
                  executingActions={executingActions}
                  isExecuting={isExecuting}
                  onOverrideExecution={() => setExecutionOverridden(true)}
                />
              );
            } else if (actionType === "Coach.Plan.Save" && actionData) {
              return <CoachPlanActionRenderer actionData={actionData} />;
            } else if (actionType === "Priority.Add" && actionData) {
              return <PriorityActionRenderer actionData={actionData} />;
            } else if (actionType === "Todo.Add" && actionData) {
              return <TodoActionRenderer actionData={actionData} />;
            } else if (actionType === "Calendar.Entry.Move" && actionData) {
              return <CalendarMoveActionRenderer actionData={actionData} />;
            } else if ((actionType === "Calendar.Entry.Create" || actionType === "Calendar.Entry.Update") && actionData) {
              return <CalendarActionRenderer actionData={actionData} />;
            } else {
              return <GenericActionRenderer actionData={actionData} metadata={metadata} messageContent={message.content} />;
            }
          })()}

          {/* Execute button for proposed actions (excluding email actions and others that override execution) */}
          {isProposed && !isExecuting && !isFailed && metadata.actionId && onExecuteAction && !executionOverridden && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={executingActions?.has(metadata.actionId) ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              disabled={executingActions?.has(metadata.actionId)}
              onClick={() => {
                if (message.actionType === "Email.Draft.Create" && message.data) {
                  openGmailDraft(message.data.to, message.data.subject, message.data.body);
                  setTimeout(() => onExecuteAction(metadata.actionId!), 500); // Mark as executed after opening
                } else {
                  onExecuteAction(metadata.actionId!);
                }
              }}
              sx={{ mr: 1 }}
            >
              {executingActions?.has(metadata.actionId) ? "Executing..." : isExecuting ? "Executing..." : message.actionType === "Email.Draft.Create" ? "Open Draft in Gmail" : "Execute Action"}
            </Button>
          )}

          {/* Success button for executed actions */}
          {isExecuted && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckIcon />}
              disabled
              sx={{
                mr: 1,
                backgroundColor: "#4caf50",
                "&:hover": { backgroundColor: "#45a049" },
              }}
            >
              Executed
            </Button>
          )}

          {/* Ignore button for proposed actions */}
          {isProposed && !isExecuting && !isFailed && metadata.actionId && onExecuteAction && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<CloseIcon />}
              onClick={() => {
                if (onIgnoreAction && metadata.actionId) {
                  onIgnoreAction(metadata.actionId);
                }
              }}
              sx={{ mr: 1 }}
            >
              Ignore
            </Button>
          )}

          {/* Expand button */}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            }
          >
            {expanded ? "Hide Details" : "Show Details"}
          </Button>

          {/* Expandable details */}
          {expanded && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />

              {/* Action metadata */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Action Details
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {metadata.actionId && <Chip label={`Action ID: ${metadata.actionId}`} size="small" color={isExecuted ? "success" : isProposed ? "warning" : "default"} variant="outlined" />}
                  {metadata.actionStatus && <Chip label={`Status: ${metadata.actionStatus}`} size="small" color={isExecuted ? "success" : isProposed ? "warning" : "default"} variant="outlined" />}
                </Box>

                {/* Action data summary */}
                {message.data && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Action Parameters
                    </Typography>
                    <Box sx={{ p: 1, bgcolor: "rgba(0,0,0,0.05)", borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {message.data.title && `Title: ${message.data.title}`}
                        {message.data.description && ` • Description: ${message.data.description}`}
                        {message.data.category && ` • Category: ${message.data.category}`}
                        {message.data.priority && ` • Priority: ${message.data.priority}`}
                        {message.data.deadline && ` • Due: ${message.data.deadline}`}
                        {message.data.subject && ` • Subject: ${message.data.subject}`}
                        {message.data.to && ` • To: ${message.data.to}`}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Raw action data */}
                {message.data && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="caption">View Raw Action Data</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <pre style={{ fontSize: "0.75rem", overflow: "auto" }}>{JSON.stringify(message.data, null, 2)}</pre>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Raw metadata */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="caption">View Raw Metadata</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre style={{ fontSize: "0.75rem", overflow: "auto" }}>{JSON.stringify(metadata, null, 2)}</pre>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </ListItem>
  );
};

// Source message component - compact centered line design like events
const SourceMessage: React.FC<{ message: ChatMessage; userTimezone?: string }> = ({ message, userTimezone }) => {
  const [expanded, setExpanded] = useState(false);
  const date = toDate(message.timestamp);
  const metadata = message.metadata || {};

  // Convert calendar event data for display in user's timezone
  const convertCalendarDataForDisplay = (data: any): any => {
    if (!data || !userTimezone) return data;

    // If this is calendar event data, convert times to user timezone
    if (Array.isArray(data) && data.length > 0 && data[0].startTime) {
      return data.map((event: any) => ({
        ...event,
        startTime: event.startTime
          ? TimezoneUtils.formatDateInTimezone(new Date(event.startTime), userTimezone, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : event.startTime,
        endTime: event.endTime
          ? TimezoneUtils.formatDateInTimezone(new Date(event.endTime), userTimezone, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : event.endTime,
      }));
    }

    // If this is a single calendar event
    if (data.startTime && data.endTime) {
      return {
        ...data,
        startTime: TimezoneUtils.formatDateInTimezone(new Date(data.startTime), userTimezone, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        endTime: TimezoneUtils.formatDateInTimezone(new Date(data.endTime), userTimezone, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      };
    }

    return data;
  };

  let icon = <InfoIcon sx={{ fontSize: 16, color: "grey.600" }} />;
  let bgcolor = "grey.100";
  let borderColor = "grey.300";

  if (message.type === "source_response") {
    icon = <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />;
    bgcolor = "success.light";
    borderColor = "success.main";
  } else if (message.type === "source_request") {
    icon = <InfoIcon sx={{ fontSize: 16, color: "info.main" }} />;
    bgcolor = "info.light";
    borderColor = "info.main";
  } else if (metadata.error) {
    icon = <ErrorIcon sx={{ fontSize: 16, color: "error.main" }} />;
    bgcolor = "error.light";
    borderColor = "error.main";
  }

  return (
    <ListItem sx={{ px: 0, py: 1 }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Centered source line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: bgcolor,
            border: "1px solid",
            borderColor: borderColor,
            cursor: "pointer",
            "&:hover": {
              bgcolor: bgcolor === "grey.100" ? "grey.200" : bgcolor,
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {icon}
          <Typography variant="caption" color="white">
            {message.content}
          </Typography>
          {date && (
            <Typography variant="caption" color="white">
              {userTimezone
                ? TimezoneUtils.formatDateInTimezone(date, userTimezone, {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : date.toLocaleTimeString()}
            </Typography>
          )}
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              color: "grey.600",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </Box>

        {/* Expandable details */}
        {expanded && (
          <Paper sx={{ mt: 1, p: 2, width: "100%", maxWidth: 600 }}>
            <Typography variant="subtitle2" gutterBottom>
              {message.type === "source_response" ? "Source Response Details" : "Source Request Details"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {message.content}
            </Typography>

            {/* Source metadata */}
            {metadata.sourceId && <Chip label={`Source: ${metadata.sourceId}`} size="small" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />}

            {metadata.reason && <Chip label={`Reason: ${metadata.reason}`} size="small" color="secondary" variant="outlined" sx={{ mr: 1, mb: 1 }} />}

            {/* Source data */}
            {metadata.data && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="caption">View Source Data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ fontSize: "0.75rem", overflow: "auto" }}>{JSON.stringify(convertCalendarDataForDisplay(metadata.data), null, 2)}</pre>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Error information */}
            {metadata.error && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="caption">View Error Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ fontSize: "0.75rem", overflow: "auto", color: "error.main" }}>{JSON.stringify(metadata.error, null, 2)}</pre>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Raw metadata */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption">View Raw Metadata</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre style={{ fontSize: "0.75rem", overflow: "auto" }}>{JSON.stringify(metadata, null, 2)}</pre>
              </AccordionDetails>
            </Accordion>
          </Paper>
        )}
      </Box>
    </ListItem>
  );
};

export const MessageItem: React.FC<MessageItemProps> = ({ message, onExecuteAction, onIgnoreAction, executingActions, agentNames, agentAvatars, userTimezone }) => {
  const date = toDate(message.timestamp);
  const [expanded, setExpanded] = useState(false);
  const metadata = message.metadata || {};
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Get current user for display name
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return unsubscribe;
  }, []);

  // Get agent name and avatar for this message if it's an agent message
  const isAgentMessage = message.type === "agent";
  const agentName = isAgentMessage && message.agentId && agentNames ? agentNames[message.agentId] : undefined;
  const agentAvatar = isAgentMessage && message.agentId && agentAvatars ? agentAvatars[message.agentId] : undefined;

  // Special handling for events
  if (message.type === "event") {
    return <EventMessage message={message} userTimezone={userTimezone} />;
  }

  // Special handling for actions
  if (message.type === "action") {
    return <ActionMessage message={message} onExecuteAction={onExecuteAction} executingActions={executingActions} />;
  }

  // Special handling for source messages
  if (message.type === "source") {
    return <SourceMessage message={message} userTimezone={userTimezone} />;
  }

  // Special handling for new message types
  if (message.type === "source_request" || message.type === "source_response") {
    return <SourceMessage message={message} userTimezone={userTimezone} />;
  }

  if (message.type === "action_proposal" || message.type === "action_execution") {
    return <ActionMessage message={message} onExecuteAction={onExecuteAction} onIgnoreAction={onIgnoreAction} executingActions={executingActions} />;
  }

  // Regular message rendering for user/agent/system
  const isUserMessage = message.type === "user";

  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        px: 0,
        position: "relative",
        display: "flex",
        width: "100%",
        justifyContent: isUserMessage ? "flex-end" : "flex-start",
        gap: 1,
      }}
    >
      {!isUserMessage && (
        <ListItemAvatar sx={{ minWidth: "auto", mr: 2 }}>
          {isAgentMessage ? (
            <AgentAvatar avatarPath={agentAvatar} name={agentName || message.agentId} size={40} />
          ) : (
            <Avatar sx={{ bgcolor: getMessageColor(message.type) }}>{getMessageIcon(message.type)}</Avatar>
          )}
        </ListItemAvatar>
      )}
      <Paper
        sx={{
          p: 2,
          bgcolor: message.type === "user" ? "primary.main" : "grey.50",
          color: message.type === "user" ? "primary.contrastText" : "inherit",
          width: isUserMessage ? "fit-content" : "100%",
          maxWidth: "70%",
          wordBreak: "break-word",
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {message.type === "user"
            ? currentUser?.displayName || currentUser?.email || "User"
            : isAgentMessage
              ? agentName || message.agentId
              : message.type.charAt(0).toUpperCase() + message.type.slice(1)}
          {date && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {date.toLocaleString()}
            </Typography>
          )}
        </Typography>
        <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
          {renderMarkdown(message.content)}
        </Typography>

        {/* Metadata chips */}
        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {metadata.eventType && <Chip label={`Event: ${metadata.eventType}`} size="small" color="warning" variant="outlined" />}
          {metadata.sourceId && <Chip label={`Source: ${metadata.sourceId}`} size="small" color="secondary" variant="outlined" />}
        </Box>

        {/* Metadata data accordion */}
        {metadata.data && (
          <Accordion expanded={expanded} onChange={() => setExpanded((v) => !v)} sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="caption">View Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ fontSize: "0.75rem", overflow: "auto" }}>{JSON.stringify(metadata.data, null, 2)}</pre>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
      {isUserMessage && (
        <ListItemAvatar sx={{ minWidth: "auto", ml: 2 }}>
          <Avatar sx={{ bgcolor: getMessageColor(message.type) }}>{getMessageIcon(message.type)}</Avatar>
        </ListItemAvatar>
      )}
    </ListItem>
  );
};
